import { useExecuteCommandMutation } from '@redux/api'

import { executeCommandAction, useOpenForm } from '@hooks'
import { executeCode, flattenFieldValues, getValuesFromVariables } from '@helpers'
import { ACTION_CODE } from '@constants'
import { ActionRow, GETAllVariables, GETVariableDTO, ObjectDataRecord, Row } from '@types'

import { EVENT_NAME, publish } from '../events'
import { handleOpenFormAction } from '@components/DisplayForm/helpers'

export type FieldChangeVariableContext = {
  row: Row | undefined
  formVariables: GETVariableDTO[]
  outerVariables: GETAllVariables | undefined
  actions: ActionRow[]
  uniqueEventId: string
  executeCommand?: ReturnType<typeof useExecuteCommandMutation>[0]
  openForm: ReturnType<typeof useOpenForm>['openForm']
}

const buildScriptContext = (
  row: Row | undefined,
  dropdownValue: unknown,
  fieldsArray: Row,
  formVariables: GETVariableDTO[],
  outerVariables: GETAllVariables | undefined
) => {
  const resolvedOuterVariables = getValuesFromVariables(outerVariables)

  return {
    // старые скрипты лезут напрямую в srcObj, поэтому оставляем для совместимости
    srcObj: { ...row, dropdownValue, outerVariables: resolvedOuterVariables },
    dropdownValue,
    rowData: row,
    currentRowValues: flattenFieldValues(fieldsArray),
    formValues: fieldsArray,
    formVariables,
    outerVariables: resolvedOuterVariables,
    enrichedObject: row,
  }
}

const resolveNewValue = (
  jsValue: string | null | undefined,
  context: Record<string, unknown>,
  rawFieldValue: unknown
): unknown => {
  if (jsValue?.trim()) {
    const result = executeCode(jsValue, context)

    return result === false ? '' : result
  }

  return typeof rawFieldValue === 'object' ? (rawFieldValue as { id?: unknown })?.id : rawFieldValue
}

/**
 * Обрабатывает изменение поля формы — ищет связанные CHANGE_VARIABLE_VALUE actions,
 * вычисляет значение (через jsValue или напрямую) и публикует события.
 *
 * Используется в двух местах:
 * - useChangeVariableWithAction (реактивно, через watch)
 * - handleSetFieldValuesOnFormInit (императивно, после setValue при инициализации)
 */
export const processFieldChangeVariable = (
  fieldsArray: Row,
  fieldName: string,
  ctx: FieldChangeVariableContext
) => {
  const changeVarActions = ctx.actions.filter(
    action =>
      action.actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE &&
      action.objectFieldCode === fieldName
  )

  const openFormActions = ctx.actions.filter(
    action =>
      (action.actionCode === ACTION_CODE.OPEN_ANOTHER_FORM_MODEL ||
        action.actionCode === ACTION_CODE.OPEN_FORM_MODEL) &&
      action.objectFieldCode === fieldName
  )

  if (!changeVarActions.length && !openFormActions.length) return

  const newFieldValue = fieldsArray[fieldName]
  const context = buildScriptContext(
    ctx.row,
    newFieldValue,
    fieldsArray,
    ctx.formVariables,
    ctx.outerVariables
  )
  const currentTime = new Date().toISOString()

  const publishVariableChange = (action: ActionRow, commandResponse?: ObjectDataRecord) => {
    const value = resolveNewValue(
      action.jsValue,
      {
        ...context,
        commandResponse,
      },
      newFieldValue
    )

    // скрипт не вернул значение — ни одно условие не выполнилось, переменную не трогаем
    if (action.jsValue?.trim() && value === undefined) return

    const newVariableValue = {
      ...action.variable,
      value,
      updatedOn: currentTime,
    }

    setTimeout(() => {
      publish(EVENT_NAME.CHANGE_VARIABLE_VALUE, newVariableValue, ctx.uniqueEventId)
    }, 0)
  }

  openFormActions.forEach(action => {
    handleOpenFormAction({
      context,
      action,
      executeCommand: ctx.executeCommand,
      formVariables: ctx.formVariables,
      openForm: ctx.openForm,
    })
  })

  changeVarActions.forEach(action => {
    if (action.command && ctx.executeCommand) {
      executeCommandAction({
        action,
        executeCommand: ctx.executeCommand,
        context,
      }).then(commandResponse => {
        publishVariableChange(action, commandResponse)
      })

      return
    }

    publishVariableChange(action)
  })
}
